import {mulberry32, xmur3} from './string-utils';

export interface GrungeMaskOptions {
    size: number;             // canvas width/height
    blurPx: number;           // optional blur for soft edges
    holeChance: number;       // fraction of pixels fully transparent
    partialHoleChance: number;// fraction of pixels partially transparent
    numClusters: number;      // base number of larger worn patches
    maxClusterSize: number;   // max size of a cluster in pixels
}

export function generateGrungeMask(
    key: string,
    options: Partial<GrungeMaskOptions> = {}
): string {
    const config: GrungeMaskOptions = {
        size: 64,               // suitable for ~50px icons
        blurPx: 0.5,
        holeChance: 0.05,
        partialHoleChance: 0.1,
        numClusters: 12,
        maxClusterSize: 8,
        ...options
    };

    const canvas: HTMLCanvasElement = document.createElement('canvas');
    canvas.width = config.size;
    canvas.height = config.size;
    const ctx: CanvasRenderingContext2D = canvas.getContext('2d')!;
    const imageData: ImageData = ctx.createImageData(config.size, config.size);
    const data: ImageDataArray = imageData.data;

    // Base PRNG
    const seedFn: () => number = xmur3(key);
    const rand: () => number = mulberry32(seedFn());

    const fullHoleThreshold: number = 1 - config.holeChance;
    const partialHoleThreshold: number = 1 - (config.holeChance + config.partialHoleChance);

    // Fill base pixels with mostly solid ink
    for (let y: number = 0; y < config.size; y++) {
        for (let x: number = 0; x < config.size; x++) {
            const idx: number = (y * config.size + x) * 4;
            const n: number = rand();
            const alpha: number = n > fullHoleThreshold ? 0
                : n > partialHoleThreshold ? 180
                    : 255;
            data[idx] = 255;
            data[idx + 1] = 255;
            data[idx + 2] = 255;
            data[idx + 3] = alpha;
        }
    }

    // Separate PRNG for clusters
    const clusterRand: () => number = mulberry32(seedFn() + 1);

    // Deterministic variation in number of clusters
    const minClusters: number = Math.max(1, Math.floor(config.numClusters / 2));
    const maxClusters: number = config.numClusters;
    const numClustersActual: number = Math.floor(clusterRand() * (maxClusters - minClusters + 1)) + minClusters;

    // Add larger “no-ink” clusters with irregular shapes
    for (let c = 0; c < numClustersActual; c++) {
        const clusterSizeActual: number = Math.floor(clusterRand() * (config.maxClusterSize - 2 + 1)) + 2;
        const clusterX: number = Math.floor(clusterRand() * config.size);
        const clusterY: number = Math.floor(clusterRand() * config.size);

        for (let dx: number = 0; dx < clusterSizeActual; dx++) {
            for (let dy: number = 0; dy < clusterSizeActual; dy++) {
                const px: number = clusterX + dx;
                const py: number = clusterY + dy;
                if (px < config.size && py < config.size) {
                    // Randomly skip pixels for irregular edges
                    if (clusterRand() < 0.35) continue;

                    const idx: number = (py * config.size + px) * 4;
                    // Partial worn effect: alpha 50–75% for some pixels
                    const alphaReduction: number = clusterRand() < 0.3
                        ? 128 + Math.floor(clusterRand() * 64)   // 128–191
                        : 0;
                    data[idx + 3] = data[idx + 3] > alphaReduction ? alphaReduction : data[idx + 3];
                }
            }
        }

        // Optional micro-clusters around main cluster
        const numMicro: number = Math.floor(clusterRand() * 3); // 0–2 tiny clusters
        for (let m = 0; m < numMicro; m++) {
            const microX: number = clusterX + Math.floor(clusterRand() * clusterSizeActual) - 1;
            const microY: number = clusterY + Math.floor(clusterRand() * clusterSizeActual) - 1;
            if (microX >= 0 && microY >= 0 && microX < config.size && microY < config.size) {
                const idx: number = (microY * config.size + microX) * 4;
                const alphaReduction: number = 128 + Math.floor(clusterRand() * 64);
                data[idx + 3] = data[idx + 3] > alphaReduction ? alphaReduction : data[idx + 3];
            }
        }
    }

    ctx.putImageData(imageData, 0, 0);

    // Optional blur
    if (config.blurPx > 0) {
        ctx.filter = `blur(${config.blurPx}px)`;
        ctx.drawImage(canvas, 0, 0);
    }

    return canvas.toDataURL('image/png');
}
